import type { QualitySample, SessionQuality } from "../api/parse.ts";

export type ViewerLink = {
  rttMs: number | null;
  jitterMs: number | null;
  packetLoss: number | null;
};

export type ViewerCounters = {
  packetsLost: number;
  packetsReceived: number;
  at: number;
};

export type ViewerReading = ViewerLink & { at: number };

type StatBag = {
  type?: unknown;
  kind?: unknown;
  nominated?: unknown;
  state?: unknown;
  currentRoundTripTime?: unknown;
  jitter?: unknown;
  packetsLost?: unknown;
  packetsReceived?: unknown;
  timestamp?: unknown;
};

type StatReport = {
  forEach(read: (stat: StatBag) => void): void;
};

const POLL_FLOOR_MS = 2000;

export function readViewerStats(
  report: StatReport,
  previous: ViewerCounters | null,
): { link: ViewerLink; counters: ViewerCounters | null } {
  let rttSeconds: number | null = null;
  let jitterSeconds: number | null = null;
  let packetsLost: number | null = null;
  let packetsReceived: number | null = null;
  let at: number | null = null;
  report.forEach((stat) => {
    if (stat.type === "candidate-pair" && stat.nominated === true && stat.state === "succeeded") {
      rttSeconds = finite(stat.currentRoundTripTime);
    }
    if (stat.type === "inbound-rtp" && stat.kind === "video") {
      jitterSeconds = finite(stat.jitter);
      packetsLost = finite(stat.packetsLost);
      packetsReceived = finite(stat.packetsReceived);
      at = finite(stat.timestamp);
    }
  });
  const counters =
    packetsLost !== null && packetsReceived !== null && at !== null
      ? { packetsLost, packetsReceived, at }
      : previous;
  return {
    link: {
      rttMs: rttSeconds === null ? null : rttSeconds * 1000,
      jitterMs: jitterSeconds === null ? null : jitterSeconds * 1000,
      packetLoss: lossFraction(previous, packetsLost, packetsReceived),
    },
    counters,
  };
}

export function watchViewerLink(
  read: () => Promise<ViewerLink | null>,
  onReading: (reading: ViewerReading) => void,
  intervalMs = POLL_FLOOR_MS,
  now = Date.now,
): () => void {
  let stopped = false;
  const timer = setInterval(() => {
    void read().then((link) => {
      if (stopped || !link) return;
      onReading({ at: now(), rttMs: link.rttMs, jitterMs: link.jitterMs, packetLoss: link.packetLoss });
    });
  }, Math.max(POLL_FLOOR_MS, intervalMs));
  return () => {
    stopped = true;
    clearInterval(timer);
  };
}

export function applyViewerReadings(quality: SessionQuality | null, readings: ViewerReading[]): SessionQuality | null {
  if (!quality || readings.length === 0) return quality;
  return {
    ...quality,
    samples: quality.samples.map((sample, index) => {
      const reading = readingByOrder(index, quality.samples.length, readings);
      return fillSample(sample, reading);
    }),
  };
}

function readingByOrder(index: number, sampleCount: number, readings: ViewerReading[]): ViewerReading | undefined {
  return readings[readings.length - sampleCount + index];
}

function fillSample(sample: QualitySample, reading: ViewerReading | undefined): QualitySample {
  if (!reading) return sample;
  return {
    ...sample,
    rttMs: sample.rttMs ?? reading.rttMs,
    jitterMs: sample.jitterMs ?? reading.jitterMs,
    packetLoss: sample.packetLoss ?? reading.packetLoss,
  };
}

function lossFraction(previous: ViewerCounters | null, lost: number | null, received: number | null): number | null {
  if (!previous || lost === null || received === null) return null;
  const lostDelta = lost - previous.packetsLost;
  const receivedDelta = received - previous.packetsReceived;
  const total = lostDelta + receivedDelta;
  if (lostDelta < 0 || receivedDelta < 0 || total <= 0) return null;
  return lostDelta / total;
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
